import React from 'react';
import styled from 'styled-components';
import { Menu, Button, MenuButton, Box, Avatar, Stack, Text, Tooltip, Flex } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { UilLinkAlt, UilAngleDown, UilEye, UilArrowToBottom } from '@iconscout/react-unicons';
import { useQuery } from '@apollo/client';
import queries from '../../utils/query';
import PopoverCreateProject from '../popover/PopoverCreateEditProject';
import theme from '../../utils/ChakraTheme';

import { ChakraProvider } from '@chakra-ui/react';
import { createStandaloneToast } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { visualizationMode } from '../../redux/visualizationModeSlice';
import PopoverUploadImages from '../popover/PopoverUploadImages';
import ResetPageBlocksButton from '../header/ResetPageBlocksButton';
import SendToCompileBtn from '../header/SendToCompileBtn';
import OpenCanvas from './OpenCanvas';
import SelectCanvasResolutionSA from './SelectCanvasResolutionSA';

const HeaderContainer = styled.div`
	width: 100%;
	top: 0;
	height: 72px;
	background-color: #ffffff;
	box-shadow: 0px 1px 3px 0px #0000001a;
	display: inline-flex;
	justify-content: space-between;
	z-index: 1000;
`;

const y300 = { bg: 'yellow.300', borderColor: 'yellow.300' };
const y400 = { bg: 'yellow.400', borderColor: 'yellow.400' };

function getStage(stage) {
	const stageName = {
		'at-creation': 'Em criação',
		'post-approval': 'Pós Aprovação',
		approved: 'Finalizado',
	};
	return stageName[stage] ? stageName[stage] : '';
}

function onImageClick(navigate) {
	navigate('/');
}

const logo = `data:image/png;base64, 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`;
const HeaderSAWM = ({
	userInfo,
	viewId,
	isOpen,
	onClose,
	onOpen,
	isDebugActive,
	setIsDebugActive,
	setLeftDrawerIsOpen,
	leftDrawerIsOpen,
}) => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { loading, error, data } = useQuery(queries.GET_VIEW_LIST(viewId), { skip: !viewId });
	const [projectData, setProjectData] = React.useState(false);
	const VMMode = useSelector(visualizationMode);

	const toast = createStandaloneToast();

	React.useEffect(() => {
		if (data && data.viewList && data.viewList.viewId) {
			setProjectData(data.viewList);
		}
	}, [data]);

	React.useEffect(() => {
		if (error) {
			toast({
				title: `Ocorreu um erro ao buscar o header do projeto: ${error}`,
				status: 'error',
				duration: 10000,
				isClosable: true,
				position: 'top',
			});
		}
	}, [error]);

	if (VMMode) {
		//Para renderizar os elementos, será preciso um estágio mais avançado de desenvolvimento. (ter componentes)
		return null;
	}

	return (
		<ChakraProvider theme={theme} resetCSS={false}>
			<HeaderContainer className="mz-hefesto-header">
				<Box
					display={{ lg: 'flex', md: 'flex', sm: 'none' }}
					_hover={{ cursor: 'pointer' }}
					as="img"
					w={'160px'}
					objectFit={'none'}
					src={logo}
					onClick={() => onImageClick(navigate)}
				/>
				{projectData ? (
					<Box d="flex" justifyContent="space-between" flexGrow={1} flexDir="Row" alignSelf="center">
						<Box display={{ lg: 'flex', md: 'none', sm: 'none' }} flexDir="Row" alignSelf="center" ml={'4%'}>
							<Stack spacing={0}>
								<Tooltip hasArrow aria-label="Nome do projeto" label={projectData.viewName}>
									<Text isTruncated fontWeight="700" maxW={'275px'} color="gray.700" fontSize="md">
										{projectData.viewName}
									</Text>
								</Tooltip>
								<Text color="gray.700" fontSize="sm">
									{getStage(projectData.stage)}
								</Text>
							</Stack>
							<Box>
								<PopoverCreateProject
									{...{
										setProjectData,
										projectData,
										isOpen,
										onClose,
										onOpen,
										popoverPosition: 'bottom-start',
										...{ ...projectData },
										isCanvasPage: true,
									}}
								/>
							</Box>
						</Box>
						<Stack
							display={{ lg: 'flex', md: 'flex', sm: 'none' }}
							position={'relative'}
							direction="row"
							alignItems="center"
							spacing={4}
							mr={4}
						>
							<SelectCanvasResolutionSA />
							{process.env.NODE_ENV == 'development' ? <ResetPageBlocksButton /> : null}
						</Stack>
						<Flex
							ml={{ md: 0, lg: 0, sm: 4 }}
							position={'relative'}
							direction="row"
							wrap={'wrap'}
							alignItems="center"
							sx={{ gap: '8px' }}
							mr={4}
						>
							<Button
								borderColor="gray.200"
								lineHeight={6}
								h={'auto'}
								px={3}
								py={0}
								m={0}
								fontSize={'xs'}
								variant="outline"
								onClick={(e) => setLeftDrawerIsOpen(!leftDrawerIsOpen)}
							>
								Menu lateral
							</Button>
							<Button
								onClick={() => {
									setIsDebugActive(!isDebugActive);
								}}
								borderColor="gray.200"
								lineHeight={6}
								h={'auto'}
								px={3}
								py={0}
								m={0}
								fontSize={'xs'}
								rightIcon={<UilLinkAlt size={12} color="gray.700" />}
								variant="outline"
							>
								Toggle Debugger
							</Button>
							<PopoverUploadImages {...{ viewId }} />
							{/* <Button borderColor='gray.200' lineHeight={6}  h={'auto'} px={3} py={0} my={0} fontSize={'xs'} rightIcon={<UilLinkAlt size={12} color='gray.700'/>} variant='outline'>
                                Gerar Link do Projeto
                            </Button> */}
							<SendToCompileBtn {...{ viewId, accountName: projectData.accountName }}></SendToCompileBtn>
							<OpenCanvas viewId={viewId} />
						</Flex>
					</Box>
				) : (
					''
				)}
			</HeaderContainer>
		</ChakraProvider>
	);
};
const HeaderSA = React.memo(HeaderSAWM);
export default HeaderSA;
